import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../firebaseConfig';
import Header from '../components/Header';

const TOUR_OPTIONS = [
  { id: "san-juan", label: "San Juan, Puerto Rico" },
  { id: "mexico-city-scavenger-hunt-tour", label: "Mexico City, Mexico" },
  { id: "chicago-scavenger-hunt-tour", label: "Chicago, Illinois" }
];

export default function GiftLookup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [selectedTour, setSelectedTour] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null); // null=no search, []=no matches, [...]=matches

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setResults(null);
    setLoading(true);

    try {
      // Validate inputs
      if (!email || !selectedTour || !purchaseDate) {
        setError("Please fill in all fields");
        setLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Please enter a valid email address");
        setLoading(false);
        return;
      }

      // Query all gift codes
      const giftCodesRef = ref(db, 'giftCodes');
      const snapshot = await get(giftCodesRef);

      if (!snapshot.exists()) {
        setResults([]);
        setLoading(false);
        return;
      }

      const allGiftCodes = snapshot.val();
      const normalizedEmail = email.toLowerCase().trim();
      // Input date is already in YYYY-MM-DD format from the date input
      const inputDate = purchaseDate;

      // Filter matching codes
      const matches = [];
      for (const [giftCode, data] of Object.entries(allGiftCodes)) {
        // Match email (case-insensitive)
        if (data.purchasedBy?.toLowerCase() !== normalizedEmail) continue;

        // Match tourId
        if (data.tourId !== selectedTour) continue;

        // Match purchase date with +/- 1 day tolerance for timezone differences
        // purchaseDate is stored as ISO string like "2024-12-20T15:30:00.000Z"
        const codePurchaseDate = data.purchaseDate ? data.purchaseDate.split('T')[0] : '';

        // Calculate day difference (force UTC interpretation to avoid timezone shifts)
        const inputDateObj = new Date(inputDate + 'T00:00:00Z');
        const codeDateObj = new Date(codePurchaseDate + 'T00:00:00Z');
        const daysDiff = Math.abs((inputDateObj - codeDateObj) / (1000 * 60 * 60 * 24));

        // Allow match if within 1 day
        if (daysDiff > 1) continue;

        matches.push({
          code: giftCode,
          ...data
        });
      }

      setResults(matches);
      setLoading(false);

      // If exactly 1 match, redirect immediately
      if (matches.length === 1) {
        navigate(`/gift-certificate?session_id=${matches[0].stripeSessionId}`);
      }

    } catch (err) {
      console.error("Gift lookup error:", err);
      setError("An error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col text-gray-800 overflow-hidden">
      {/* Background layers */}
      <div
        className="fixed inset-0 -z-10"
        style={{ background: 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)' }}
      />
      <div
        className="fixed inset-0 -z-10"
        style={{ background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)' }}
      />
      <div
        className="fixed inset-0 -z-10 opacity-[.04] pointer-events-none"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      <Header />

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-center px-4 mt-20 mb-12 relative z-10">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-3xl shadow-md p-8 md:p-10">
          <h1
            className="text-3xl font-bold text-center mb-2"
            style={{ fontFamily: 'Rubik, sans-serif', color: '#385B37' }}
          >
            Find Your Gift Certificate
          </h1>
          <p className="text-gray-600 text-center mb-6 text-sm">
            Enter the email used to purchase the gift certificate
          </p>

          <form onSubmit={handleLookup} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#385B37' }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-3 rounded-lg border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition"
                style={{
                  borderColor: '#E4B4A6',
                  '--tw-ring-color': 'rgba(143, 179, 211, 0.6)'
                }}
              />
            </div>

            {/* Tour Selection */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#385B37' }}>
                Tour Location
              </label>
              <select
                value={selectedTour}
                onChange={(e) => setSelectedTour(e.target.value)}
                required
                className="w-full p-3 rounded-lg border text-gray-900 focus:outline-none focus:ring-2 transition"
                style={{
                  borderColor: '#E4B4A6',
                  '--tw-ring-color': 'rgba(143, 179, 211, 0.6)'
                }}
              >
                <option value="">Select a tour...</option>
                {TOUR_OPTIONS.map(tour => (
                  <option key={tour.id} value={tour.id}>{tour.label}</option>
                ))}
              </select>
            </div>

            {/* Purchase Date */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#385B37' }}>
                Purchase Date
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
                className="w-full p-3 rounded-lg border text-gray-900 focus:outline-none focus:ring-2 transition"
                style={{
                  borderColor: '#E4B4A6',
                  '--tw-ring-color': 'rgba(143, 179, 211, 0.6)'
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-white shadow-md hover:shadow-lg active:scale-95 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#F8662B' }}
            >
              {loading ? "Searching..." : "Find Certificate"}
            </button>
          </form>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-red-700 text-sm text-center font-medium">
                {error}
              </p>
            </div>
          )}

          {/* No Results */}
          {results !== null && results.length === 0 && (
            <div className="mt-4 p-4 rounded-xl border-2 bg-red-50" style={{ borderColor: '#E4B4A6' }}>
              <p className="text-center text-red-700 font-semibold">
                No gift certificates found
              </p>
              <p className="text-center text-sm text-gray-600 mt-2">
                Please verify your email, tour selection, and purchase date.
              </p>
            </div>
          )}

          {/* Multiple Results */}
          {results !== null && results.length > 1 && (
            <div className="mt-6">
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: '#385B37', fontFamily: 'Rubik, sans-serif' }}
              >
                Found {results.length} Gift Certificates
              </h3>
              <div className="space-y-3">
                {results.map((result) => (
                  <div
                    key={result.code}
                    className="p-4 rounded-xl border bg-white hover:shadow-md transition"
                    style={{ borderColor: '#E4B4A6' }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold" style={{ color: '#385B37' }}>
                          To: {result.personalization?.to || "Gift Recipient"}
                        </p>
                        <p className="text-sm text-gray-600">
                          From: {result.personalization?.from || "Anonymous"}
                        </p>
                      </div>
                      <p className="text-xs font-mono text-gray-500">{result.code}</p>
                    </div>
                    {result.personalization?.message && (
                      <p className="text-sm italic text-gray-600 mb-3">
                        "{result.personalization.message}"
                      </p>
                    )}
                    <button
                      onClick={() => navigate(`/gift-certificate?session_id=${result.stripeSessionId}`)}
                      className="w-full py-2 rounded-lg text-white font-semibold transition hover:opacity-90"
                      style={{ backgroundColor: '#F8662B' }}
                    >
                      View Certificate
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Support Link */}
          <p className="text-center text-gray-600 mt-6 text-sm">
            Can't find your gift certificate?{" "}
            <a
              href="mailto:hello@tourinabox.com"
              className="font-semibold transition hover:opacity-70"
              style={{ color: '#F8662B' }}
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
